type TurnstileApi = {
  render: (container: HTMLElement, options: {
    sitekey: string;
    action: string;
    execution: 'execute';
    appearance: 'interaction-only';
    callback: (token: string) => void;
    'error-callback': () => void;
    'expired-callback': () => void;
  }) => string;
  execute: (widgetId: string) => void;
  remove: (widgetId: string) => void;
};

declare global {
  interface Window { turnstile?: TurnstileApi }
}

let scriptPromise: Promise<TurnstileApi> | undefined;

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  scriptPromise ??= new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.onload = () => window.turnstile ? resolve(window.turnstile) : reject(new Error('Verification could not load.'));
    script.onerror = () => reject(new Error('Verification could not load.'));
    document.head.appendChild(script);
  }).catch((error: unknown) => {
    scriptPromise = undefined;
    throw error;
  });
  return scriptPromise;
}

async function getTurnstileToken(action: string): Promise<string> {
  const sitekey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;
  if (!sitekey) throw new Error('Verification is unavailable. Please try again later.');

  const turnstile = await loadTurnstile();
  const container = document.createElement('div');
  container.style.cssText = 'position:fixed;bottom:16px;right:16px;z-index:2147483647';
  document.body.appendChild(container);
  let widgetId: string | undefined;
  try {
    return await new Promise<string>((resolve, reject) => {
      widgetId = turnstile.render(container, {
        sitekey,
        action,
        execution: 'execute',
        appearance: 'interaction-only',
        callback: resolve,
        'error-callback': () => reject(new Error('Verification failed. Please try again.')),
        'expired-callback': () => reject(new Error('Verification expired. Please try again.')),
      });
      turnstile.execute(widgetId);
    });
  } finally {
    if (widgetId) turnstile.remove(widgetId);
    container.remove();
  }
}

/** Gets a fresh, single-use token for each email-producing request. */
export async function protectedFetch(url: string, init: RequestInit, action: string): Promise<Response> {
  const token = await getTurnstileToken(action);
  const headers = new Headers(init.headers);
  headers.set('x-turnstile-token', token);
  headers.set('x-turnstile-action', action);
  return fetch(url, { ...init, headers });
}
